import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

const startOfToday = (zone?: string) =>
  (zone ? DateTime.now().setZone(zone) : DateTime.now()).startOf('day');

/**
 * Today's calendar date as an ISO date, refreshed when the day rolls over so
 * "now"-anchored windows don't go stale on a long-lived tab.
 *
 * Exposed as a memo dependency rather than a value: the calendar day is all
 * that changes at midnight, and callers re-read DateTime.now() themselves.
 *
 * Pass `zone` to track the day in a specific timezone (e.g. an experience's
 * display timezone); omit it to follow the viewer's own. The rollover is
 * scheduled against the same zone, so a tab in one timezone showing a schedule
 * in another turns over on the schedule's midnight, not the viewer's.
 */
export const useTodayIso = (zone?: string): string | null => {
  // State is only a re-render trigger. The day itself is read from the clock on
  // every render, so it is also correct immediately when `zone` changes rather
  // than staying on the previous zone's day until the next midnight.
  const [, setRollover] = useState(0);
  const today = startOfToday(zone).toISODate();

  useEffect(() => {
    const current = startOfToday(zone);

    // The day is read during render but the timer is armed here, so midnight
    // can fall between the two. Scheduling from `current` would then aim a full
    // day ahead while the rendered value stayed on yesterday — stale until the
    // NEXT midnight. Re-render instead; the following pass agrees and arms the
    // timer properly.
    if (current.toISODate() !== today) {
      setRollover((n) => n + 1);
      return;
    }

    const msUntilMidnight = current.plus({ days: 1 }).diffNow().toMillis();
    const timer = setTimeout(
      () => setRollover((n) => n + 1),
      // A zone whose offset just shifted can compute a non-positive delay; fire
      // on the next tick rather than never.
      Math.max(0, msUntilMidnight)
    );
    return () => clearTimeout(timer);
  }, [today, zone]);

  return today;
};
